import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getStickyNotes, createStickyNote, updateStickyNote, deleteStickyNote, bringStickyNoteToFront } from '../api/stickyNotesApi';
import rpcSettings from '../api/rpc/settings';
import {
  FiX,
  FiMaximize2,
  FiMinimize2,
  FiMove,
  FiPlus,
  FiTrash2,
  FiEdit3,
  FiSave,
  FiLock,
  FiUnlock,
  FiCheckSquare,
  FiSearch,
} from 'react-icons/fi';
import { useClient } from '../contexts/ClientContext';

const getAuthHeaders = () => {
  if (typeof window === 'undefined') return undefined;
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

interface StickyNote {
  _id: string;
  content: string;
  color: string;
  position: { x: number; y: number };
  zIndex: number;
  isPinned: boolean;
  isMinimized: boolean;
  width: number;
  height: number;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
}

type TodoItem = {
  id: string;
  text: string;
  done: boolean;
};

interface DragState {
  isDragging: boolean;
  noteId: string | null;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

type ResizeAxis = 'both' | 'horizontal' | 'vertical';

interface ResizeState {
  isResizing: boolean;
  noteId: string | null;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  axis: ResizeAxis;
}

const COLORS = [
  { value: '#fef68a', name: 'צהוב' },
  { value: '#fecaca', name: 'אדום' },
  { value: '#bbf7d0', name: 'ירוק' },
  { value: '#bfdbfe', name: 'כחול' },
  { value: '#e9d5ff', name: 'סגול' },
  { value: '#fed7aa', name: 'כתום' },
  { value: '#fbcfe8', name: 'ורוד' },
  { value: '#d1d5db', name: 'אפור' },
];

const TODO_PREFIX = '__TODO_JSON__::';
const DEFAULT_TITLE = 'פתק ללא שם';

// Date formatter utility
const formatDateShort = (dateString?: string): string => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    // Format as DD.MM.YYYY (Hebrew date format)
    const dateParts = d.toLocaleDateString('en-GB', {
      timeZone: 'Asia/Jerusalem',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).split('/');
    return `${dateParts[0]}.${dateParts[1]}.${dateParts[2]}`;
  } catch {
    return '—';
  }
};

// Debounced value hook
function useDebounced<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

function getTenantFromPathname(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0] || '';
  // Skip only public routes that don't have a company context
  if (['login', 'privacy', 'features', 'pricing', 'about', 'contact', 'crm-for-small-business', 'crm-appointments'].includes(seg)) return '';
  return seg;
}

const StickyNotes: React.FC = () => {
  const location = useLocation();
  const { client } = useClient();
  const [notes, setNotes] = useState<StickyNote[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [showManager, setShowManager] = useState(false);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    noteId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    noteId: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    axis: 'both',
  });
  const [selectedColor, setSelectedColor] = useState('#fef68a');
  const [titlesCache, setTitlesCache] = useState<Record<string, string>>({});
  const saveTimeoutRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const dragStateRef = useRef(dragState);
  const tenant = getTenantFromPathname(location.pathname);
  
  // Search, filter, sort, and pagination state
  const [search, setSearch] = useState('');
  const [selectedColorFilter, setSelectedColorFilter] = useState<'all' | string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounced(search, 300);
  
  const titlesStorageKey = useMemo(
    () => (tenant ? `sticky-note-titles:${tenant}` : null),
    [tenant]
  );
  
  // Re-register event listeners when tenant changes (after company switch)
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | null>(null);
  const [featureChecked, setFeatureChecked] = useState(false);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const isCreatingNoteRef = useRef(false);

  const ensureTitle = useCallback(
    (note: StickyNote): StickyNote => ({
      ...note,
      title: typeof note.title === 'string' ? note.title : DEFAULT_TITLE,
    }),
    []
  );

  const loadTitlesFromStorage = useCallback(() => {
    if (!titlesStorageKey) return {};
    try {
      const raw = localStorage.getItem(titlesStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }, [titlesStorageKey]);

  useEffect(() => {
    setTitlesCache(loadTitlesFromStorage());
  }, [loadTitlesFromStorage]);

  const persistTitle = useCallback(
    (noteId: string, title?: string) => {
      setTitlesCache((prev) => {
        const next = { ...prev };
        const trimmed = title?.trim();
        if (!trimmed || trimmed === DEFAULT_TITLE) {
          delete next[noteId];
        } else {
          next[noteId] = trimmed;
        }
        if (titlesStorageKey) {
          try {
            localStorage.setItem(titlesStorageKey, JSON.stringify(next));
          } catch {
            // ignore storage failures
          }
        }
        return next;
      });
    },
    [titlesStorageKey]
  );

  const applyTitle = useCallback(
    (note: StickyNote): StickyNote => {
      // Prioritize title from database (note.title), fallback to localStorage cache, then default
      const ensured = ensureTitle(note);
      // Only use localStorage cache if database doesn't have a title
      if (ensured.title && ensured.title !== DEFAULT_TITLE) {
        return ensured;
      }
      const override = titlesCache[ensured._id];
      return override ? { ...ensured, title: override } : ensured;
    },
    [titlesCache]
  );

  useEffect(() => {
    setNotes((prev) => prev.map(applyTitle));
  }, [applyTitle]);

  const isTodoContent = useCallback(
    (content: string) => content.startsWith(TODO_PREFIX),
    []
  );

  const decodeTodos = useCallback(
    (content: string): TodoItem[] => {
      if (!isTodoContent(content)) return [];
      try {
        const raw = content.slice(TODO_PREFIX.length);
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => ({
              id:
                typeof item?.id === 'string'
                  ? item.id
                  : Math.random().toString(36).slice(2, 10),
              text: typeof item?.text === 'string' ? item.text : '',
              done: Boolean(item?.done),
            }))
            .filter((item) => item.text.trim().length > 0 || !item.done);
        }
      } catch {
        // ignore parse errors
      }
      return [];
    },
    [isTodoContent]
  );

  const encodeTodos = useCallback(
    (todos: TodoItem[]) => `${TODO_PREFIX}${JSON.stringify(todos)}`,
    [TODO_PREFIX]
  );

  const generateId = useCallback(
    () =>
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2, 10),
    []
  );
  
  // Keep dragStateRef in sync with dragState
  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);

  // Check if user has permission (admin, agent, manager)
  const hasPermission = ['admin', 'agent', 'manager'].includes(client?.role || '');

  // Load company features
  useEffect(() => {
    if (!client) {
      return;
    }
    if (client?.role === 'superadmin' || !tenant || tenant === 'superadmin') {
      setEnabledFeatures(null); // null means allow all
      setFeatureChecked(true);
      return;
    }

    const fetchFeatures = async () => {
      const headers = getAuthHeaders();
      if (!headers) {
        setEnabledFeatures(null);
        setFeatureChecked(true);
        return;
      }

      try {
        // Get company features directly using company slug (works for any user in the company)
        const { data } = await rpcSettings.getCompanyFeatures();
        const features = data?.enabledFeatures || [];
        setEnabledFeatures(features);
      } catch (err: any) {
        // Silently handle timeout errors and 404/403 - don't log
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
        if (err?.response?.status !== 404 && err?.response?.status !== 403 && !isTimeout) {
          // Only log non-timeout, non-404, non-403 errors
          console.error('Failed to fetch company features:', err);
        }
        setEnabledFeatures(null);
      } finally {
        setFeatureChecked(true);
      }
    };

    fetchFeatures();
  }, [tenant, client?.role]);

  // Listen for feature updates
  useEffect(() => {
    if (!client) {
      return;
    }
    if (client?.role === 'superadmin' || !tenant || tenant === 'superadmin') {
      return;
    }

    const handleFeatureUpdate = () => {
      const fetchFeatures = async () => {
        const headers = getAuthHeaders();
        if (!headers) {
          setEnabledFeatures(null);
          setFeatureChecked(true);
          return;
        }

        try {
          // Get company features directly using company slug
          const { data } = await rpcSettings.getCompanyFeatures();
          const features = data?.enabledFeatures || [];
          setEnabledFeatures(features);
        } catch (err: any) {
          console.error('Failed to fetch company features:', err);
          setEnabledFeatures(null);
        } finally {
          setFeatureChecked(true);
        }
      };
      fetchFeatures();
    };

    window.addEventListener('company-features-updated', handleFeatureUpdate as EventListener);
    return () => {
      window.removeEventListener('company-features-updated', handleFeatureUpdate as EventListener);
    };
  }, [tenant, client?.role]);

  useEffect(() => {
    if (!hasPermission) {
      return;
    }

    // Check if sticky-notes feature is enabled
    // If enabledFeatures is null, allow (not loaded yet or superadmin)
    // If enabledFeatures is empty array [], no features enabled
    // If enabledFeatures has items, check if sticky-notes is in the list
    if (enabledFeatures !== null && enabledFeatures.length > 0) {
      if (!enabledFeatures.includes('sticky-notes')) {
        // Feature not enabled - don't listen to events or fetch notes
        return;
      }
    }

    // Only fetch notes if we have a valid token (user is authenticated)
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (tenant && token) {
      fetchNotes();
    }
    
    // Listen for sidebar click to toggle manager panel
    const handleToggle = (e: Event) => {
      setShowManager(prev => !prev);
    };
    
    window.addEventListener('sticky-notes', handleToggle as EventListener);
    return () => {
      window.removeEventListener('sticky-notes', handleToggle as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, hasPermission, enabledFeatures, featureChecked]);

  const fetchNotes = async () => {
    try {
      const response = await getStickyNotes(tenant);
      
      // Merge with existing notes to preserve any ongoing drag state
      setNotes(prevNotes => {
        if (prevNotes.length === 0) {
          // First load - just set the notes
          return ((response as any).notes || []).map(applyTitle);
        }
        
        // Update existing notes but preserve position if currently dragging
        const currentDragState = dragStateRef.current;
        const isDragging = currentDragState.isDragging;
        const draggedNoteId = currentDragState.noteId;
        
        return ((response as any).notes || []).map((serverNote: StickyNote) => {
          // If this note is being dragged, keep local position
          if (isDragging && serverNote._id === draggedNoteId) {
            const localNote = prevNotes.find(n => n._id === serverNote._id);
            return applyTitle(
              localNote
                ? { ...serverNote, position: localNote.position }
                : serverNote
            );
          }
          return applyTitle(serverNote);
        });
      });
    } catch (err: any) {
      // Silently handle timeout errors and 403 errors - don't log
      const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
      if (err.response?.status !== 403 && !isTimeout) {
        // Only log non-timeout, non-403 errors
        console.error('Failed to fetch sticky notes:', err);
      }
    }
  };

  const createNote = async () => {
    // Prevent duplicate submissions
    if (isCreatingNoteRef.current || isCreatingNote) return;
    
    if (!tenant) {
      return;
    }
    
    setIsCreatingNote(true);
    isCreatingNoteRef.current = true;
    
    try {
      const payload = {
        content: '',
        color: selectedColor,
        position: { x: 100 + notes.length * 20, y: 100 + notes.length * 20 },
        title: DEFAULT_TITLE,
      };
      const response = await createStickyNote(tenant, payload);
      setNotes(prev => [...prev, applyTitle((response as any).note)]);
      persistTitle((response as any).note._id, DEFAULT_TITLE);
    } catch (err: any) {
      console.error('Failed to create note:', err);
      console.error('Error response:', err.response?.data);
      // Error is handled silently - user can see the result in the UI
    } finally {
      setIsCreatingNote(false);
      isCreatingNoteRef.current = false;
    }
  };

  const updateNote = async (noteId: string, updates: Partial<StickyNote>, skipStateUpdate = false) => {
    try {
      const response = await updateStickyNote(tenant, noteId, updates);
      
      // Skip state update if requested (used during drag/resize to avoid conflicts)
      if (skipStateUpdate) {
        return;
      }
      
      // Only update specific fields from server response to avoid overwriting local changes
      setNotes(prevNotes => prevNotes.map((n) => {
        if (n._id !== noteId) return n;

        const serverNote: StickyNote = (response as any).note;
        const merged: StickyNote = {
          ...n,
          ...serverNote,
        };

        // Preserve local position/size if those fields were not part of the update payload
        if (updates.position === undefined) {
          merged.position = n.position;
        }
        if (updates.width === undefined) {
          merged.width = n.width;
        }
        if (updates.height === undefined) {
          merged.height = n.height;
        }

        // Preserve local content if the current update originated from typing and the user may
        // have continued typing while the request was in-flight.
        if (updates.content !== undefined) {
          const contentSent = updates.content;
          const localMatchesSent = n.content === contentSent;
          merged.content = localMatchesSent ? serverNote.content : n.content;
        }

        return applyTitle(merged);
      }));
    } catch (err) {
      console.error('Failed to update note:', err);
      toast.error('שגיאה בעדכון פתק');
    }
  };

  const deleteNote = async (noteId: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הפתק?')) return;
    
    try {
      await deleteStickyNote(tenant, noteId);
      setNotes(notes.filter((n) => n._id !== noteId));
      persistTitle(noteId, undefined);
      toast.success('פתק נמחק בהצלחה');
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error('שגיאה במחיקת פתק');
    }
  };

  const bringToFront = async (noteId: string) => {
    try {
      const response = await bringStickyNoteToFront(tenant, noteId);
      // Only update zIndex, keep other properties (especially position) unchanged
      setNotes(prevNotes => prevNotes.map((n) => 
        n._id === noteId ? { ...n, zIndex: (response as any)?.note?.zIndex || n.zIndex } : n
      ));
    } catch (err) {
      console.error('Failed to bring note to front:', err);
    }
  };

  const debouncedSave = (noteId: string, updates: Partial<StickyNote>, skipStateUpdate = false) => {
    if (saveTimeoutRef.current[noteId]) {
      clearTimeout(saveTimeoutRef.current[noteId]);
    }
    saveTimeoutRef.current[noteId] = setTimeout(() => {
      updateNote(noteId, updates, skipStateUpdate);
    }, 500);
  };

  const handleMouseDown = (e: React.MouseEvent, noteId: string, note: StickyNote) => {
    if (note.isPinned) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate offset from the note's current position (not the header rect)
    // This ensures the note doesn't jump when you start dragging
    const offsetX = e.clientX - note.position.x;
    const offsetY = e.clientY - note.position.y;
    
    setDragState({
      isDragging: true,
      noteId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX,
      offsetY,
    });
    
    bringToFront(noteId);
  };

  const handleResizeMouseDown = (
    e: React.MouseEvent,
    noteId: string,
    note: StickyNote,
    axis: ResizeAxis = 'both'
  ) => {
    e.preventDefault();
    e.stopPropagation();
    
    setResizeState({
      isResizing: true,
      noteId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: note.width,
      startHeight: note.height,
      axis,
    });
    bringToFront(noteId);
  };

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleMouseMove = (e: MouseEvent) => {
      // Cancel any pending animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      // Use requestAnimationFrame for smooth 60fps updates
      rafId = requestAnimationFrame(() => {
        if (dragState.isDragging && dragState.noteId) {
          // Get viewport dimensions
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Calculate new position
          let newX = e.clientX - dragState.offsetX;
          let newY = e.clientY - dragState.offsetY;

          // Get the note to access its dimensions
          setNotes((prevNotes) => {
            const note = prevNotes.find((n) => n._id === dragState.noteId);
            if (!note) return prevNotes;

            // Constrain position to keep note within viewport bounds
            // Minimum: 0 (top-left corner)
            // Maximum: viewport size - note size (so note doesn't go off-screen)
            const minX = 0;
            const maxX = Math.max(0, viewportWidth - note.width);
            const minY = 0;
            const maxY = Math.max(0, viewportHeight - note.height);

            // Clamp the position within bounds
            newX = Math.max(minX, Math.min(maxX, newX));
            newY = Math.max(minY, Math.min(maxY, newY));

            // Use functional update to avoid stale closure
            return prevNotes.map((n) =>
              n._id === dragState.noteId
                ? { ...n, position: { x: newX, y: newY } }
                : n
            );
          });
        }

        if (resizeState.isResizing && resizeState.noteId) {
          const deltaX = e.clientX - resizeState.startX;
          const deltaY = e.clientY - resizeState.startY;

          const rawWidth =
            resizeState.axis === 'vertical'
              ? resizeState.startWidth
              : resizeState.startWidth + deltaX;
          const rawHeight =
            resizeState.axis === 'horizontal'
              ? resizeState.startHeight
              : resizeState.startHeight + deltaY;

          // Get viewport dimensions for constraints
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // Constrain size: min size (200x160) and max size (800x800)
          // Also ensure note doesn't exceed viewport when resized
          let newWidth = Math.max(200, Math.min(800, rawWidth));
          let newHeight = Math.max(160, Math.min(800, rawHeight));

          // Get the note to check its position and ensure it stays within viewport
          setNotes((prevNotes) => {
            const note = prevNotes.find((n) => n._id === resizeState.noteId);
            if (!note) return prevNotes;

            // If resizing would make the note go outside viewport, constrain the size
            // Ensure: note.position.x + newWidth <= viewportWidth
            // Ensure: note.position.y + newHeight <= viewportHeight
            const maxWidth = viewportWidth - note.position.x;
            const maxHeight = viewportHeight - note.position.y;

            newWidth = Math.min(newWidth, Math.max(200, maxWidth));
            newHeight = Math.min(newHeight, Math.max(160, maxHeight));

            // Use functional update to avoid stale closure
            return prevNotes.map((n) =>
              n._id === resizeState.noteId
                ? { ...n, width: newWidth, height: newHeight }
                : n
            );
          });
        }
      });
    };

    const handleMouseUp = () => {
      if (dragState.isDragging && dragState.noteId) {
        const noteIdToSave = dragState.noteId; // Capture the ID
        // Use callback to get the latest state
        setNotes((currentNotes) => {
          const note = currentNotes.find((n) => n._id === noteIdToSave);
          if (note) {
            // Save to server but DON'T update state (skipStateUpdate=true)
            // This prevents the server response from overwriting our local position
            updateNote(noteIdToSave, { position: note.position }, true);
          }
          return currentNotes; // Don't change state, just read it
        });
        
        setDragState({
          isDragging: false,
          noteId: null,
          startX: 0,
          startY: 0,
          offsetX: 0,
          offsetY: 0,
        });
      }

      if (resizeState.isResizing && resizeState.noteId) {
        const noteIdToSave = resizeState.noteId; // Capture the ID
        // Use callback to get the latest state
        setNotes((currentNotes) => {
          const note = currentNotes.find((n) => n._id === noteIdToSave);
          if (note) {
            // Save to server but DON'T update state (skipStateUpdate=true)
            updateNote(noteIdToSave, { width: note.width, height: note.height }, true);
          }
          return currentNotes; // Don't change state, just read it
        });
        
        setResizeState({
          isResizing: false,
          noteId: null,
          startX: 0,
          startY: 0,
          startWidth: 0,
          startHeight: 0,
          axis: 'both',
        });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, resizeState]);

  const handleContentChange = (noteId: string, content: string) => {
    // Update local state immediately for smooth typing
    setNotes(prevNotes => prevNotes.map((n) => (n._id === noteId ? { ...n, content } : n)));
    // Debounce the API call
    debouncedSave(noteId, { content });
  };

  const handleTitleChange = (noteId: string, title: string) => {
    setNotes(prevNotes =>
      prevNotes.map((n) => (n._id === noteId ? { ...n, title } : n))
    );
    // Save to database immediately (debounced)
    debouncedSave(noteId, { title });
    // Also save to localStorage as backup
    persistTitle(noteId, title);
  };

  const handleTitleBlur = (noteId: string, title: string) => {
    // Clear any pending debounced save
    if (saveTimeoutRef.current[noteId]) {
      clearTimeout(saveTimeoutRef.current[noteId]);
      delete saveTimeoutRef.current[noteId];
    }
    
    const finalTitle = title.trim().length === 0 ? DEFAULT_TITLE : title.trim();
    
    setNotes(prevNotes =>
      prevNotes.map((n) =>
        n._id === noteId ? { ...n, title: finalTitle } : n
      )
    );
    
    // Save to database immediately (no debounce on blur)
    updateNote(noteId, { title: finalTitle });
    persistTitle(noteId, finalTitle);
  };

  const handleTodoUpdate = (noteId: string, updater: (todos: TodoItem[]) => TodoItem[]) => {
    let nextContent: string | null = null;
    setNotes(prevNotes =>
      prevNotes.map((n) => {
        if (n._id !== noteId) return n;
        const currentTodos = decodeTodos(n.content);
        const updatedTodos = updater(currentTodos).filter(
          (todo) => todo.text.trim().length > 0 || todo.done
        );
        nextContent = encodeTodos(updatedTodos);
        return { ...n, content: nextContent };
      })
    );
    if (nextContent !== null) {
      debouncedSave(noteId, { content: nextContent });
    }
  };

  const toggleTodoMode = (noteId: string, note: StickyNote) => {
    if (isTodoContent(note.content)) {
      const todos = decodeTodos(note.content);
      const text = todos.length
        ? todos
            .map((todo) => `${todo.done ? '[x]' : '[ ]'} ${todo.text}`)
            .join('\n')
        : '';
      setNotes(prevNotes =>
        prevNotes.map((n) => (n._id === noteId ? { ...n, content: text } : n))
      );
      debouncedSave(noteId, { content: text });
    } else {
      const lines = note.content
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const todos =
        lines.length > 0
          ? lines.map((line) => {
              const done = /^\s*(\[x\]|✔|✓)/i.test(line);
              const text = line
                .replace(/^\s*(\[x\]|\[ \]|✔|✓)\s*/i, '')
                .replace(/^[-*]\s*/, '')
                .trim();
              return {
                id: generateId(),
                text: text || 'משימה חדשה',
                done,
              };
            })
          : [
              {
                id: generateId(),
                text: 'משימה חדשה',
                done: false,
              },
            ];
      const encoded = encodeTodos(todos);
      setNotes(prevNotes =>
        prevNotes.map((n) => (n._id === noteId ? { ...n, content: encoded } : n))
      );
      debouncedSave(noteId, { content: encoded });
    }
  };

  const addTodoItem = (noteId: string) => {
    handleTodoUpdate(noteId, (todos) => [
      ...todos,
      { id: generateId(), text: 'משימה חדשה', done: false },
    ]);
  };

  const updateTodoText = (noteId: string, todoId: string, text: string) => {
    handleTodoUpdate(noteId, (todos) =>
      todos.map((todo) => (todo.id === todoId ? { ...todo, text } : todo))
    );
  };

  const toggleTodoDone = (noteId: string, todoId: string) => {
    handleTodoUpdate(noteId, (todos) =>
      todos.map((todo) =>
        todo.id === todoId ? { ...todo, done: !todo.done } : todo
      )
    );
  };

  const removeTodoItem = (noteId: string, todoId: string) => {
    handleTodoUpdate(noteId, (todos) => todos.filter((todo) => todo.id !== todoId));
  };

  const insertTodoAfter = (noteId: string, todoId: string) => {
    let inserted = false;
    handleTodoUpdate(noteId, (todos) => {
      const result: TodoItem[] = [];
      todos.forEach((todo) => {
        result.push(todo);
        if (todo.id === todoId) {
          result.push({ id: generateId(), text: '', done: false });
          inserted = true;
        }
      });
      if (!inserted) {
        result.push({ id: generateId(), text: '', done: false });
      }
      return result;
    });
  };

  const toggleMinimize = (noteId: string, note: StickyNote) => {
    updateNote(noteId, { isMinimized: !note.isMinimized });
  };

  const togglePin = (noteId: string, note: StickyNote) => {
    updateNote(noteId, { isPinned: !note.isPinned });
  };

  const changeColor = (noteId: string, color: string) => {
    updateNote(noteId, { color });
  };

  // Filter, sort, and paginate notes (hooks must be called before early returns)
  const filteredAndSortedNotes = useMemo(() => {
    let filtered = notes;

    // Search filter
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.trim().toLowerCase();
      filtered = filtered.filter((note) => {
        const title = (note.title || DEFAULT_TITLE).toLowerCase();
        const content = (note.content || '').toLowerCase();
        return title.includes(searchLower) || content.includes(searchLower);
      });
    }

    // Color filter
    if (selectedColorFilter !== 'all') {
      filtered = filtered.filter((note) => note.color === selectedColorFilter);
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'newest') {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate; // Descending (newest first)
      } else if (sortBy === 'oldest') {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return aDate - bDate; // Ascending (oldest first)
      } else {
        // Sort by name
        const aTitle = (a.title || DEFAULT_TITLE).toLowerCase();
        const bTitle = (b.title || DEFAULT_TITLE).toLowerCase();
        return aTitle.localeCompare(bTitle, 'he');
      }
    });

    return filtered;
  }, [notes, debouncedSearch, selectedColorFilter, sortBy]);

  // Pagination
  const totalCount = filteredAndSortedNotes.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedNotes = filteredAndSortedNotes.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedColorFilter, sortBy]);

  if (!hasPermission) {
    return null;
  }

  // Check if sticky-notes feature is enabled
  // If enabledFeatures is null, allow (not loaded yet or superadmin)
  // If enabledFeatures is empty array [], no features enabled
  // If enabledFeatures has items, check if sticky-notes is in the list
  if (featureChecked && enabledFeatures !== null && enabledFeatures.length > 0) {
    if (!enabledFeatures.includes('sticky-notes')) {
      return null;
    }
  }

  // Generate page numbers for pagination
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <>
      {/* Manager Panel - Positioned next to sidebar */}
      {showManager && (
        <>
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-[9998] transition-opacity"
            onClick={() => setShowManager(false)}
          />
          
          {/* Panel */}
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] bg-white rounded-2xl shadow-2xl p-4 sm:p-6 w-[95vw] sm:w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col animate-fadeIn"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FiEdit3 className="text-blue-500" />
                ניהול פתקים
              </h3>
              <button
                onClick={() => setShowManager(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 transition-all"
                title="סגור"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Color Picker + Create Button */}
            <div className="mb-4 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  בחר צבע לפתק חדש:
                </label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setSelectedColor(color.value)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColor === color.value
                          ? 'border-gray-800 scale-110'
                          : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={createNote}
                disabled={isCreatingNote}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isCreatingNote ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>יוצר פתק...</span>
                  </>
                ) : (
                  <>
                    <FiPlus size={20} />
                    <span>צור פתק חדש</span>
                  </>
                )}
              </button>
            </div>

            {/* Filters & Search Bar */}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-1 items-center gap-2">
                {/* Search Input */}
                <div className="relative flex-1">
                  <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="חיפוש פתקים..."
                    className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                  />
                </div>

                {/* Color Filter */}
                <select
                  value={selectedColorFilter}
                  onChange={(e) => setSelectedColorFilter(e.target.value as 'all' | string)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
                >
                  <option value="all">כל הצבעים</option>
                  {COLORS.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.name}
                    </option>
                  ))}
                </select>

                {/* Sort Dropdown */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
                >
                  <option value="newest">חדש ביותר</option>
                  <option value="oldest">ישן ביותר</option>
                  <option value="name">לפי שם</option>
                </select>
              </div>

              {/* Page Size Select */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 whitespace-nowrap">גודל עמוד:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                  className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Notes List - Scrollable */}
            <div className="mt-3 flex-1 overflow-y-auto pr-1" style={{ maxHeight: '50vh' }}>
              {totalCount === 0 ? (
                <div className="text-center py-8">
                  <FiEdit3 size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">
                    {notes.length === 0
                      ? 'אין פתקים פעילים'
                      : 'לא נמצאו פתקים התואמים לחיפוש'}
                  </p>
                  {notes.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      לחץ על "צור פתק חדש" להתחלה
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {paginatedNotes.map((note) => (
                    <div
                      key={note._id}
                      className="flex items-center justify-between rounded-xl border bg-white px-3 py-2 shadow-sm hover:shadow transition-all"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span
                          className="inline-flex h-6 w-6 rounded-full border-2 border-gray-300 flex-shrink-0"
                          style={{ backgroundColor: note.color }}
                        />
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {note.title?.trim().length ? note.title : DEFAULT_TITLE}
                          </span>
                          <span className="text-xs text-gray-500">
                            נוצר בתאריך {formatDateShort(note.createdAt)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {note.isPinned && (
                          <FiLock size={14} className="text-gray-400" title="נעול" />
                        )}
                        <button
                          onClick={() => toggleMinimize(note._id, note)}
                          className="text-gray-500 hover:text-blue-600 hover:bg-blue-50 p-1.5 rounded transition-all"
                          title={note.isMinimized ? 'הרחב' : 'מזער'}
                        >
                          {note.isMinimized ? (
                            <FiMaximize2 size={16} />
                          ) : (
                            <FiMinimize2 size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => deleteNote(note._id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded transition-all"
                          title="מחק"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination Bar */}
            {totalPages > 1 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row justify-center items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-3 py-1.5 rounded-lg border transition-colors text-sm ${
                      page === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                    }`}
                  >
                    קודם
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((pageNum, index) => {
                      if (pageNum === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-400 text-sm">
                            ...
                          </span>
                        );
                      }
                      const pageNumber = pageNum as number;
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setPage(pageNumber)}
                          className={`px-3 py-1.5 rounded-lg border transition-colors text-sm ${
                            page === pageNumber
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className={`px-3 py-1.5 rounded-lg border transition-colors text-sm ${
                      page >= totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                    }`}
                  >
                    הבא
                  </button>
                </div>

                {/* Total Count */}
                <div className="text-center mt-2">
                  <span className="text-xs text-gray-500">
                    מציג {startIndex + 1}-{Math.min(endIndex, totalCount)} מתוך {totalCount} פתקים
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Sticky Notes */}
      {notes
        .filter((note) => !note.isMinimized)
        .map((note) => (
          <div
            key={note._id}
            className="fixed shadow-2xl rounded-lg overflow-hidden transition-shadow duration-200 hover:shadow-3xl"
            style={{
              left: note.position.x,
              top: note.position.y,
              width: note.width,
              height: note.height,
              zIndex: note.zIndex,
              backgroundColor: note.color,
              direction: 'rtl',
              cursor: note.isPinned ? 'default' : 'move',
              border: note.isPinned ? '2px solid rgba(0,0,0,0.3)' : '1px solid rgba(0,0,0,0.1)',
              pointerEvents: 'auto',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center gap-2 p-2 border-b border-gray-300 bg-opacity-50 bg-gray-100"
              onMouseDown={(e) => !note.isPinned && handleMouseDown(e, note._id, note)}
              style={{
                cursor: note.isPinned ? 'not-allowed' : 'grab',
                userSelect: 'none',
              }}
            >
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => deleteNote(note._id)}
                  className="p-1 hover:bg-red-200 rounded transition-colors"
                  title="מחק"
                >
                  <FiX size={16} />
                </button>
                <button
                  onClick={() => toggleMinimize(note._id, note)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="מזער"
                >
                  <FiMinimize2 size={16} />
                </button>
                <button
                  onClick={() => togglePin(note._id, note)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title={note.isPinned ? 'בטל נעיצה' : 'נעץ'}
                >
                  {note.isPinned ? <FiLock size={16} /> : <FiUnlock size={16} />}
                </button>
                <button
                  onClick={() => toggleTodoMode(note._id, note)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title={isTodoContent(note.content) ? 'המר לטקסט חופשי' : 'רשימת משימות'}
                >
                  {isTodoContent(note.content) ? <FiEdit3 size={16} /> : <FiCheckSquare size={16} />}
                </button>
              </div>

              <input
                value={note.title ?? ''}
                onChange={(e) => handleTitleChange(note._id, e.target.value)}
                onBlur={(e) => handleTitleBlur(note._id, e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex-1 bg-white/70 border border-gray-300 rounded-lg px-2 py-1 text-sm text-gray-800 font-semibold outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                placeholder={DEFAULT_TITLE}
              />

              {/* Color Selector */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {COLORS.slice(0, 4).map((color) => (
                  <button
                    key={color.value}
                    onClick={() => changeColor(note._id, color.value)}
                    className={`w-5 h-5 rounded-full border transition-all ${
                      note.color === color.value
                        ? 'border-gray-800 border-2'
                        : 'border-gray-400'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* Content */}
            {isTodoContent(note.content) ? (
              (() => {
                const todos = decodeTodos(note.content);
                return (
                  <div className="flex flex-col h-[calc(100%-3rem)] px-2 pb-2">
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                      {todos.map((todo) => (
                    <div
                      key={todo.id}
                      className="flex items-center gap-2 bg-white/70 rounded-lg px-2 py-1 shadow-sm"
                    >
                      <input
                        type="checkbox"
                        checked={todo.done}
                        onChange={() => toggleTodoDone(note._id, todo.id)}
                        className="w-4 h-4 accent-green-600"
                      />
                      <input
                        type="text"
                        value={todo.text}
                        onChange={(e) => updateTodoText(note._id, todo.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            insertTodoAfter(note._id, todo.id);
                          } else if (
                            e.key === 'Backspace' &&
                            !e.shiftKey &&
                            e.currentTarget.value === ''
                          ) {
                            e.preventDefault();
                            removeTodoItem(note._id, todo.id);
                          }
                        }}
                        className="flex-1 bg-transparent border-none outline-none text-sm text-gray-800"
                        placeholder="הקלד משימה..."
                      />
                      <button
                        onClick={() => removeTodoItem(note._id, todo.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="מחק משימה"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                      {todos.length === 0 && (
                        <div className="text-xs text-gray-500 text-center py-4">
                          אין משימות. הוסף משימה חדשה.
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => addTodoItem(note._id)}
                      className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-white/60 hover:bg-white/80 text-gray-700 text-sm font-semibold py-2 transition"
                    >
                      <FiPlus size={14} /> הוסף משימה
                    </button>
                  </div>
                );
              })()
            ) : (
              <textarea
                value={note.content}
                onChange={(e) => handleContentChange(note._id, e.target.value)}
                className="w-full h-[calc(100%-3rem)] p-3 resize-none outline-none bg-transparent text-gray-800"
                placeholder="כתוב כאן..."
                style={{
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  lineHeight: '1.5',
                }}
              />
            )}

            {/* Resize Handle */}
            {!note.isPinned && (
              <>
                <div
                  className="absolute bottom-0 left-0 w-4 h-4 cursor-se-resize hover:bg-gray-300 transition-colors"
                  onMouseDown={(e) => handleResizeMouseDown(e, note._id, note)}
                  style={{
                    background:
                      'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.3) 50%)',
                  }}
                  title="גרור לשינוי גודל"
                />
                <div
                  className="absolute top-1/2 right-0 -translate-y-1/2 w-3 h-12 cursor-ew-resize hover:bg-gray-300 transition-colors"
                  onMouseDown={(e) => handleResizeMouseDown(e, note._id, note, 'horizontal')}
                  style={{ borderRadius: '8px 0 0 8px' }}
                  title="שנה רוחב"
                />
              </>
            )}
            {/* Locked Indicator */}
            {note.isPinned && (
              <div className="absolute bottom-0 left-0 w-full p-1 text-center bg-gray-200 bg-opacity-70 text-xs text-gray-600">
                🔒 נעול
              </div>
            )}
          </div>
        ))}
    </>
  );
};

export default StickyNotes;

